import { MetricOption } from "./types";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faDollarSign,
  faShoppingCart,
  faChartLine,
  faPercent,
  faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import { ProfitSummaryData, KeyMetric } from "./types";

export const METRIC_OPTIONS: Array<{ label: string; value: MetricOption }> = [
  { label: "Gross Profit", value: "grossProfit" },
  { label: "Profit Margin", value: "profitMargin" },
  { label: "Total Revenue", value: "totalRevenue" },
  { label: "Total Cost", value: "totalCost" },
];

export const EMPTY_STATE_MESSAGES = {
  noProfitData: "No profit data available for the selected period",
} as const;

export const SUMMARY_TEXTS = {
  title: "Profit Margins Summary",
  description: "Comprehensive overview of profit margins and cost analysis.",
  marginLabel: "% margin",
  keyMetricsTitle: "Key Metrics",
} as const;

export const addCommasInPrices = (value: number | string): string => {
  return (parseFloat(String(value)) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const METRIC_CONFIG: Record<
  string,
  {
    title: string;
    subTitle: string;
    icon: IconDefinition;
    color: string;
    formatValue: (value: number) => string;
  }
> = {
  totalRevenue: {
    title: "Total Revenue",
    subTitle: "Total sales revenue",
    icon: faDollarSign,
    color: "#52c41a",
    formatValue: (value: number) => `$${addCommasInPrices(value.toFixed(2))}`,
  },
  totalCost: {
    title: "Total Cost",
    subTitle: "Total cost of goods sold",
    icon: faShoppingCart,
    color: "#ff4d4f",
    formatValue: (value: number) => `$${addCommasInPrices(value.toFixed(2))}`,
  },
  grossProfit: {
    title: "Gross Profit",
    subTitle: "Revenue minus costs",
    icon: faChartLine,
    color: "#1890ff",
    formatValue: (value: number) => `$${addCommasInPrices(value.toFixed(2))}`,
  },
  profitMargin: {
    title: "Profit Margin",
    subTitle: "Profit as percentage of revenue",
    icon: faPercent,
    color: "#722ed1",
    formatValue: (value: number) => `${value.toFixed(2)}%`,
  },
  orderCount: {
    title: "Order Count",
    subTitle: "Total number of orders",
    icon: faBullseye,
    color: "#fa8c16",
    formatValue: (value: number) => value.toString(),
  },
  itemsSold: {
    title: "Items Sold",
    subTitle: "Total items sold",
    icon: faShoppingCart,
    color: "#13c2c2",
    formatValue: (value: number) => value.toString(),
  },
};

export const TRENDING_UP_ICON_CONFIG = {
  icon: faChartLine,
  color: "#52c41a",
  fontSize: "20px",
} as const;

export const generateKeyMetrics = (summary: ProfitSummaryData): KeyMetric[] => {
  const metrics: KeyMetric[] = [];

  Object.entries(METRIC_CONFIG).forEach(([key, config]) => {
    const value = summary[key as keyof ProfitSummaryData] as number;
    if (value !== undefined) {
      metrics.push({
        id: key,
        title: config.title,
        subTitle: config.subTitle,
        value: config.formatValue(value),
        icon: config.icon,
        color: config.color,
      });
    }
  });

  return metrics;
};

export const getDefaultKeyMetrics = (): KeyMetric[] => {
  return Object.entries(METRIC_CONFIG).map(([key, config]) => ({
    id: key,
    title: config.title,
    subTitle: config.subTitle,
    value: key.includes("Revenue") || key.includes("Cost") || key.includes("Profit")
      ? "$0.00"
      : key.includes("Margin")
      ? "0.00%"
      : "0",
    icon: config.icon,
    color: config.color,
  }));
};
